import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { saveApiData } from '../../store/Slice/Global/GlobalSlice';
import { PayableApi } from '../../utils/Apis';
import { fIndianAmount, readIndianAmountInput } from '../../utils/formatNumber';
import { setApiSuccess } from '../../store/Slice/Error/ErrorSlice';

export default function PayableDialogRedux({
  openPayment,
  setOpenPayment,
  userId,
  paybleId,
  selectedPaybleId,
  invoiceAmount,
  onSuccess
}) {
  const dispatch = useDispatch();

  const PaymentMode = ['RTGS', 'NEFT', 'OTHER', 'UPI', 'BANK TRANSFER'];

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm({
    defaultValues: {
      paymentDate: '',
      invoiceAmount: '',
      tdsDeduction: '',
      gstPaid: '',
      netAmountPaid: '',
      paymentMode: '',
      accountNumber: '',
      bankName: '',
      ifscCode: ''
    }
  });

  useEffect(() => {
    if (openPayment && invoiceAmount !== undefined) {
      setValue('invoiceAmount', invoiceAmount);
    }
  }, [openPayment, invoiceAmount, setValue]);

  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (data) => {
    const payload = {
      userId,
      paybleId: selectedPaybleId || paybleId,
      paidOn: data.paymentDate,
      paymentReference: data.paymentMode,
      accountNumber: data.accountNumber,
      bankName: data.bankName,
      ifscCode: data.ifscCode,
      tdsDeduction: data.tdsDeduction
    };

    if (Number.isNaN(payload.invoiceAmount)) {
      alert('Please enter valid numeric values for all amount fields.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await dispatch(saveApiData(PayableApi?.payablePayment, payload));
      if (!res) return;
      dispatch(setApiSuccess('Paid Successfully!'));
      reset();
      setOpenPayment(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('dispatch error', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={openPayment}
      onClose={() => setOpenPayment(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: '16px', overflow: 'hidden' }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          px: 3,
          bgcolor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
          Record Payable Payment
        </Typography>
        <IconButton onClick={() => setOpenPayment(false)} size="small" sx={{ color: '#64748b' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.8, fontSize: 13, fontWeight: 600, color: '#475569' }}
                >
                  Payment Date <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="paymentDate"
                  control={control}
                  rules={{ required: 'Payment Date is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px'
                        }
                      }}
                      error={!!errors.paymentDate}
                      helperText={errors.paymentDate?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.8, fontSize: 13, fontWeight: 600, color: '#475569' }}
                >
                  Invoice Amount <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="invoiceAmount"
                  control={control}
                  rules={{
                    required: 'Invoice Amount is required',
                    validate: (v) =>
                      (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={fIndianAmount(field.value) ?? ''}
                      onChange={(e) => {
                        const rawValue = readIndianAmountInput(e);
                        if (rawValue !== null) field.onChange(rawValue);
                      }}
                      fullWidth
                      size="small"
                      type="text"
                      inputProps={{ inputMode: 'decimal' }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px'
                        }
                      }}
                      error={!!errors.invoiceAmount}
                      helperText={errors.invoiceAmount?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.8, fontSize: 13, fontWeight: 600, color: '#475569' }}
                >
                  TDS Deduction <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="tdsDeduction"
                  control={control}
                  rules={{
                    required: 'TDS Deduction is required',
                    validate: (v) =>
                      (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={fIndianAmount(field.value) ?? ''}
                      onChange={(e) => {
                        const rawValue = readIndianAmountInput(e);
                        if (rawValue !== null) field.onChange(rawValue);
                      }}
                      fullWidth
                      size="small"
                      type="text"
                      inputProps={{ inputMode: 'decimal' }}
                      placeholder="0.00"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px'
                        }
                      }}
                      error={!!errors.tdsDeduction}
                      helperText={errors.tdsDeduction?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.8, fontSize: 13, fontWeight: 600, color: '#475569' }}
                >
                  Payment Mode <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="paymentMode"
                  control={control}
                  rules={{ required: 'Payment Mode is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      fullWidth
                      size="small"
                      displayEmpty
                      sx={{
                        borderRadius: '8px'
                      }}
                      renderValue={(selected) =>
                        selected ? (
                          selected
                        ) : (
                          <span style={{ color: '#94a3b8' }}>Select payment mode</span>
                        )
                      }
                    >
                      {PaymentMode.map((mode) => (
                        <MenuItem key={mode} value={mode}>
                          {mode}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.paymentMode && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.paymentMode.message}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Account Number */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.8, fontSize: 13, fontWeight: 600, color: '#475569' }}
                >
                  Account Number <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="accountNumber"
                  control={control}
                  rules={{ required: 'Account Number is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      inputProps={{ maxLength: 20 }}
                      placeholder="Enter account number"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px'
                        }
                      }}
                      error={!!errors.accountNumber}
                      helperText={errors.accountNumber?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            {/* Bank Name */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.8, fontSize: 13, fontWeight: 600, color: '#475569' }}
                >
                  Bank Name <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="bankName"
                  control={control}
                  rules={{ required: 'Bank Name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      placeholder="Enter bank name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px'
                        }
                      }}
                      error={!!errors.bankName}
                      helperText={errors.bankName?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            {/* IFSC Code */}
            <Grid item xs={12}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.8, fontSize: 13, fontWeight: 600, color: '#475569' }}
                >
                  IFSC Code <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="ifscCode"
                  control={control}
                  rules={{ required: 'IFSC Code is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      placeholder="e.g. SBIN0001234"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px'
                        }
                      }}
                      error={!!errors.ifscCode}
                      helperText={errors.ifscCode?.message}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, px: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setOpenPayment(false)}
            variant="outlined"
            disabled={submitting}
            sx={{
              borderRadius: '8px',
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              color: '#64748b',
              borderColor: '#cbd5e1',
              '&:hover': { bgcolor: '#f1f5f9' }
            }}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{
              borderRadius: '8px',
              px: 4,
              py: 1,
              bgcolor: '#13519d',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(19, 81, 157, 0.25)',
              '&:hover': { bgcolor: '#0d3d75' }
            }}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
